import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

function RealtimeStats({ data, onRefresh }) {
  const [stats, setStats] = useState(data || {
    realtime_metrics: {
      messages_last_hour: 0,
      new_users_24h: 0,
      active_conversations: 0,
      avg_response_time_last_hour: 0.0
    },
    hourly_message_distribution: {}
  });

  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    if (data) {
      setStats(data);
      setLastUpdated(new Date());
    }
  }, [data]);

  // Generate 24-hour chart data
  const generateLast24HoursData = () => {
    const now = new Date();
    const data = [];
    
    for (let i = 23; i >= 0; i--) {
      const hour = new Date(now.getTime() - (i * 60 * 60 * 1000));
      
      // Format to match backend format: 'YYYY-MM-DD HH:00'
      const backendHourKey = hour.getFullYear() + '-' + 
                           (hour.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                           hour.getDate().toString().padStart(2, '0') + ' ' + 
                           hour.getHours().toString().padStart(2, '0') + ':00';
      
      // Also try ISO format just in case
      const isoHourKey = hour.toISOString().slice(0, 13) + ':00:00';
      
      const timeLabel = hour.getHours().toString().padStart(2, '0') + ':00';
      
      data.push({
        hour: timeLabel,
        messages: stats.hourly_message_distribution?.[backendHourKey] || 
                 stats.hourly_message_distribution?.[isoHourKey] || 0,
        timestamp: hour.getTime()
      });
    }
    
    return data;
  };

  const chartData = generateLast24HoursData();

  const handleRefresh = async () => {
    setLoading(true);
    if (onRefresh) {
      await onRefresh();
    }
    setLoading(false);
  };

  const formatResponseTime = (time) => {
    if (time < 1) {
      return `${Math.round(time * 1000)}ms`;
    }
    return `${time.toFixed(1)}s`;
  };

  const getTrend = (current, previous) => {
    if (!previous || previous === 0) return 'neutral';
    const change = ((current - previous) / previous) * 100;
    return change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const StatCard = ({ title, value, icon, color, trend, subtitle }) => (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h4" component="div" color={color}>
                {value}
              </Typography>
              {getTrendIcon(trend)}
            </Box>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main` }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            📊 Statistiche in Tempo Reale
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
            📱 Solo utenti delle piattaforme social (esclude utenti dashboard web)
          </Typography>
        </Box>
        <Tooltip title="Aggiorna dati">
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Messaggi (ultima ora)"
            value={stats.realtime_metrics?.messages_last_hour || 0}
            icon={<ChatIcon />}
            color="primary"
            trend="neutral"
            subtitle="Attività recente"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Nuovi utenti (24h)"
            value={stats.realtime_metrics?.new_users_24h || 0}
            icon={<PeopleIcon />}
            color="success"
            trend="up"
            subtitle="Crescita giornaliera"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Conversazioni attive"
            value={stats.realtime_metrics?.active_conversations || 0}
            icon={<ChatIcon />}
            color="info"
            trend="neutral"
            subtitle="In corso ora"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Tempo risposta medio"
            value={formatResponseTime(stats.realtime_metrics?.avg_response_time_last_hour || 0)}
            icon={<SpeedIcon />}
            color="warning"
            trend="down"
            subtitle="Ultima ora"
          />
        </Grid>
      </Grid>

      {chartData && chartData.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>
            📈 Messaggi nelle Ultime 24 Ore
          </Typography>
          <Box sx={{ width: '100%', height: 300, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 0,
                }}
              >
                <defs>
                  <linearGradient id="colorMessages" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#1976d2" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="hour" 
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value, name) => [
                    `${value} messaggi`,
                    'Messaggi'
                  ]}
                  labelFormatter={(label) => `Ora: ${label}`}
                />
                <Area
                  type="monotone"
                  dataKey="messages"
                  stroke="#1976d2"
                  strokeWidth={2}
                  fill="url(#colorMessages)"
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
          <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="textSecondary">
              Totale: {chartData.reduce((sum, item) => sum + item.messages, 0)} messaggi
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Media: {Math.round(chartData.reduce((sum, item) => sum + item.messages, 0) / 24)} msg/ora
            </Typography>
          </Box>
        </>
      )}

      <Box mt={2} display="flex" alignItems="center" gap={1}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'success.main',
            animation: 'pulse 2s infinite'
          }}
        />
        <Typography variant="caption" color="textSecondary">
          Ultimo aggiornamento: {lastUpdated.toLocaleTimeString('it-IT')}
        </Typography>
      </Box>
    </Paper>
  );
}

export default RealtimeStats;
